import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * The extra questions a seller asks before it will take your money.
 *
 * An emergency contact for a junior programme, a t-shirt size, a medical note.
 * Every seller asks something different, so this component is told WHAT to ask
 * rather than knowing: the caller hands checkout a field list and gets the
 * answers back. A component that knew the questions would need editing every
 * time an academy invented one.
 *
 * ─── FIELD SHAPE ────────────────────────────────────────────────────────────
 *
 *   { id, label, type, required?, options?, helpText? }
 *
 *   type: text | textarea | select | checkbox | date | phone | email
 *
 * Deliberately the same shape the programme registration form already uses, so
 * an academy's existing questions move here untouched. A second, subtly
 * different schema for the same questions is how two screens end up disagreeing
 * about which ones are required.
 *
 * ─── VALIDATION ─────────────────────────────────────────────────────────────
 *
 * `missingRequired` is exported so the step that owns the Continue button can
 * decide what to do about it. This component says what is missing; it does not
 * decide whether that blocks anyone.
 */
export function missingRequired(fields = [], values = {}) {
  return fields.filter((f) => {
    const v = values[f.id];
    // An unticked required checkbox is missing; an untouched required anything
    // else is missing. `false` is a real answer for a checkbox and an empty
    // string is not an answer for a text box.
    if (f.type === 'checkbox') return f.required && v !== true;
    return f.required && (v === undefined || v === null || v === '');
  });
}

function Field({ field, value, onChange, theme }) {
  const base = `w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input || ''} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`;
  return (
    <div>
      {field.type !== 'checkbox' && (
        <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
          {field.label}{field.required && <span className="text-red-500"> *</span>}
        </label>
      )}

      {field.type === 'textarea' ? (
        <textarea
          rows={3}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className={`${base} resize-none`}
          data-testid={`info-${field.id}`}
        />
      ) : field.type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={base}
                data-testid={`info-${field.id}`}>
          <option value="">Select…</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.type === 'checkbox' ? (
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
                 className="mt-0.5 w-4 h-4" data-testid={`info-${field.id}`} />
          <span className={`text-sm ${theme.text}`}>
            {field.label}{field.required && <span className="text-red-500"> *</span>}
          </span>
        </label>
      ) : (
        <input
          type={field.type === 'date' ? 'date'
              : field.type === 'phone' ? 'tel'
              : field.type === 'email' ? 'email' : 'text'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className={base}
          data-testid={`info-${field.id}`}
        />
      )}

      {field.helpText && <p className={`text-xs ${theme.textMuted} mt-1`}>{field.helpText}</p>}
    </div>
  );
}

export default function PaymentAdditionalInfo({
  fields = [],
  values = {},
  onChange,
  askedBy = null,
}) {
  const { theme } = useTheme();
  const missing = useMemo(() => missingRequired(fields, values), [fields, values]);

  if (!fields.length) return null;

  return (
    <div className="space-y-4">
      {askedBy && (
        <p className={`text-sm ${theme.textSecondary}`}>{askedBy} asks for the following.</p>
      )}

      {fields.map(f => (
        <Field
          key={f.id}
          field={f}
          value={values[f.id]}
          onChange={(v) => onChange?.({ ...values, [f.id]: v })}
          theme={theme}
        />
      ))}

      {/* Named, not just counted. "Some fields are required" sends people
          hunting; naming them is the difference between a hint and a help. */}
      {missing.length > 0 && (
        <p className="text-xs text-red-500">
          Still required: {missing.map(f => f.label).join(', ')}
        </p>
      )}
    </div>
  );
}
